import websocketService from '../services/websocketService';
import { clearAuth, resetApiClientState, getApiBaseUrl } from '../services/api';
import { clearSessionCookie } from './siteHosts';

export const appSessionResetEventName = 'appSessionReset';

/** localStorage keys to preserve across session resets (strictly UI preferences like theme). */
const PRESERVED_KEYS = [
  'unified-workforce-theme',
  'workfolio-theme',
];

/**
 * Purges all AI-related keys (matching uw_ai_* and legacy keys) and cached session credentials from browser storage.
 */
export function clearAiStorageAndSessionSecrets() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (
        key.startsWith('uw_ai_') ||
        key.startsWith('unified_workforce_ai_') ||
        key === 'unified_workforce_gemini_key' ||
        key === 'token' ||
        key === 'refresh_token' ||
        key === 'user' ||
        key === 'session_id' ||
        key === 'registeredUsers'
      ) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch (_) {}
}

/**
 * Hard reset of client-side session state to prevent cross-user data leakage.
 * Ensures: token, refresh_token, user, registeredUsers removed; WebSocket stopped;
 * API client reset; components listening to appSessionResetEventName re-initialize.
 * Preserves theme preference if present.
 */
export function resetAppSession(reason = 'unspecified') {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[session] resetAppSession', reason);
  }

  if (reason === 'logout') {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const refresh = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
      if (token || refresh) {
        const baseUrl = getApiBaseUrl();
        const logoutUrl = `${baseUrl.replace(/\/+$/, '')}/auth/logout/`;
        const payload = JSON.stringify({ token, refresh });
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(logoutUrl, blob);
        } else if (typeof fetch === 'function') {
          fetch(logoutUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      }
    } catch (_) {
      // ignore
    }
  }

  try {
    websocketService.stop();
  } catch (_) {
    // ignore
  }

  try {
    resetApiClientState();
  } catch (_) {
    // ignore
  }

  try {
    clearAuth();
  } catch (_) {
    // ignore
  }

  try {
    clearSessionCookie();
  } catch (_) {
    // ignore
  }

  try {
    clearAiStorageAndSessionSecrets();
    const preserved = {};
    for (const key of PRESERVED_KEYS) {
      const val = localStorage.getItem(key);
      if (val != null) preserved[key] = val;
    }
    for (const key of Object.keys(localStorage)) {
      if (
        key.startsWith('unified_workforce_payroll_') &&
        !key.startsWith('uw_ai_') &&
        !key.startsWith('unified_workforce_ai_') &&
        key !== 'unified_workforce_gemini_key'
      ) {
        preserved[key] = localStorage.getItem(key);
      }
    }
    localStorage.clear();
    for (const [key, val] of Object.entries(preserved)) {
      localStorage.setItem(key, val);
    }
    clearAiStorageAndSessionSecrets();
  } catch (_) {
    // ignore
  }

  try {
    sessionStorage.clear();
  } catch (_) {
    // ignore
  }

  try {
    window.dispatchEvent(new CustomEvent(appSessionResetEventName, { detail: { reason } }));
  } catch (_) {
    // ignore
  }

  try {
    window.dispatchEvent(new CustomEvent('userUpdated', { detail: null }));
  } catch (_) {
    // ignore
  }
}
